import { useEffect, useState } from "react";
import { Reveal, LineReveal } from "../components/motion";
import PackageCard from "../components/PackageCard";
import FAQList from "../components/FAQList";
import CTABand from "../components/CTABand";
import { api } from "../lib/api";
import { useSEO, usePageView } from "../lib/seo";

const PROCESS = [
  { step: "1", title: "Share your sleep diary", body: "Before we meet, you'll fill in a simple sleep diary so our time together goes straight to what matters." },
  { step: "2", title: "We talk it through", body: "A 60-minute consultation (Zoom or phone) where we map your child's temperament, your family's rhythm and what's realistic." },
  { step: "3", title: "Your tailored plan arrives", body: "A gentle, practical plan built for your child — not a template — with clear steps you can start that night." },
  { step: "4", title: "Support while you implement", body: "Depending on your package: follow-up notes, or weeks of WhatsApp/email guidance while the new routine settles in." },
];

export default function SupportOptions() {
  const [packages, setPackages] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useSEO({
    title: "Support Options & Pricing — The Nanny Sleep Co.",
    description: "Compare gentle sleep support packages: Sleep Reset Call $195, Personalised Sleep Plan $295, Ongoing Sleep Support $395, and the $29 Gentle Sleep Foundations Guide.",
    schema: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
  });
  usePageView("/support-options");

  useEffect(() => {
    api.packages().then(setPackages).catch(() => {});
    api.faqs().then(setFaqs).catch(() => {});
  }, []);

  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  }, [packages]);

  return (
    <div data-testid="support-options-page">
      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="overline-label mb-5">Support options</p>
          <LineReveal
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-ink"
            lines={["Honest scope.", "Honest prices.", "Real support."]}
          />
          <Reveal delay={0.5}>
            <p className="mt-7 text-lg text-ink-soft leading-relaxed">
              Every option below is defined up front: what's included, how it's delivered, and who it suits. No vague packages, no surprises.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
            {packages.map((pkg, i) => (
              <PackageCard key={pkg.id} pkg={pkg} index={i} highlight={pkg.tag === "Most chosen"} />
            ))}
          </div>
          <Reveal className="mt-8">
            <p className="text-xs text-ink-muted text-center max-w-lg mx-auto leading-relaxed">
              Prices in AUD. Not sure which fits? The <a href="/sleep-assessment" className="text-terra underline underline-offset-2">2-minute assessment</a> recommends one path — or book a free suitability call and we'll work it out together.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-warm/70 border-y border-ink/[0.05]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-12 max-w-xl">
            <p className="overline-label mb-4">How it works</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-ink leading-tight">From exhausted to a plan, in four steps</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.08}>
                <div className="bg-card rounded-2xl p-7 border border-ink/[0.06] h-full">
                  <span className="font-serif text-4xl font-light text-terra/50">{p.step}</span>
                  <h3 className="font-serif text-lg font-medium text-ink mt-3 mb-2.5">{p.title}</h3>
                  <p className="text-sm text-ink-soft leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Reveal className="mb-10">
            <p className="overline-label mb-4">Questions parents ask</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-ink">Before you decide</h2>
          </Reveal>
          <FAQList faqs={faqs} testid="support-faq" />
        </div>
      </section>

      <CTABand title="Want help choosing?" body="The free assessment recommends one path based on your child's age, what's hardest right now, and how much support you want." />
    </div>
  );
}
